import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Sparkles, RefreshCcw, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MarkdownView } from '@/components/MarkdownView';
import { relativeTime, shortDate } from '@/lib/format';
import { cn } from '@/lib/cn';

export function BriefsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: qk.briefs,
    queryFn: () => api.listBriefs(),
    refetchInterval: 10_000, // poll for generation status while page is open
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && data?.briefs.length) {
      setSelectedId(data.briefs[0]!.id);
    }
  }, [data, selectedId]);

  const generate = useMutation({
    mutationFn: () => api.generateBriefNow(),
    onSuccess: () => {
      toast.success('Brief queued. Pulls in ~30s.');
      void queryClient.invalidateQueries({ queryKey: qk.briefs });
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError
          ? `${err.code}${err.message ? ` — ${err.message}` : ''}`
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      toast.error(`Failed to queue brief: ${detail}`);
      // Also log for the browser console so the failure mode is debuggable
      // even after the toast disappears.
      console.error('briefs.generate_now_failed', err);
    },
  });

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Briefs</h1>
            <p className="text-sm text-muted-foreground">
              Daily morning brief, runs at 06:30 AEST. Earlier ones are archived here.
            </p>
          </div>
          <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
            <RefreshCcw className="h-4 w-4" />
            Generate now
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : data?.briefs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
              <BookOpen className="h-5 w-5" />
            </div>
            <p className="text-sm">No briefs yet.</p>
            <p className="max-w-md text-xs text-muted-foreground">
              The first brief generates at 06:30 AEST tomorrow. Hit Generate now to make one
              immediately.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <motion.ul
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}
            className="space-y-1.5 lg:col-span-2"
          >
            {data?.briefs.map((b) => (
              <motion.li
                key={b.id}
                variants={{
                  hidden: { opacity: 0, y: 4 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { type: 'spring', stiffness: 140, damping: 16 },
                  },
                }}
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(b.id)}
                  className={cn(
                    'w-full rounded-md border bg-card p-3 text-left transition-colors',
                    selectedId === b.id
                      ? 'border-foreground/40'
                      : 'border-border hover:border-foreground/20',
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="truncate text-sm font-medium">
                      {shortDate(b.scheduledFor)}
                    </p>
                    <Badge
                      variant={
                        b.status === 'GENERATED'
                          ? 'success'
                          : b.status === 'FAILED'
                            ? 'danger'
                            : 'warning'
                      }
                    >
                      {b.status.toLowerCase()}
                    </Badge>
                  </div>
                  <p className="mt-1 truncate text-[11px] text-muted-foreground">
                    {b.generatedAt
                      ? `generated ${relativeTime(b.generatedAt)}`
                      : `scheduled ${relativeTime(b.scheduledFor)}`}
                  </p>
                </button>
              </motion.li>
            ))}
          </motion.ul>
          <div className="lg:col-span-3">
            {selectedId && <BriefDetail id={selectedId} />}
          </div>
        </div>
      )}
    </div>
  );
}

function BriefDetail({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: qk.brief(id),
    queryFn: () => api.getBrief(id),
    refetchInterval: (q) => {
      const brief = (q.state.data as { brief?: { status: string } } | undefined)?.brief;
      return brief?.status === 'PENDING' ? 5_000 : false;
    },
  });
  if (isLoading || !data) return <Skeleton className="h-80 w-full" />;
  const b = data.brief;
  return (
    <Card className="sticky top-0">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{shortDate(b.scheduledFor)}</p>
            <p className="text-[11px] text-muted-foreground">
              {b.status === 'GENERATED' && b.generatedAt
                ? `generated ${relativeTime(b.generatedAt)}`
                : b.status.toLowerCase()}
            </p>
          </div>
          <Sparkles className="h-4 w-4 text-muted-foreground" />
        </div>

        {b.status === 'FAILED' && b.error && (
          <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            {b.error}
          </p>
        )}

        {b.status === 'PENDING' && (
          <p className="mt-4 text-sm italic text-muted-foreground">
            Generating, this updates automatically.
          </p>
        )}

        {b.contentMd && (
          <div className="mt-4 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-background p-4">
            <MarkdownView source={b.contentMd} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
